import { computed, Injectable, Signal } from '@angular/core';
import { SIDEBAR_STORAGE_KEY } from '../../constants/storage.constants';
import { createSignalSetting, SignalSetting } from '../../functions/signal-setting.function';

export type SidebarState = 'expanded' | 'collapsed';

/**
 * Narrows an arbitrary stored value to a valid {@link SidebarState}. Guards against corrupt or
 * out-of-schema data in local storage, which the primitive then discards in favour of the default.
 *
 * @param value - The raw value read back from storage.
 * @returns {boolean} True when the value is a recognised sidebar state.
 */
export function isSidebarState(value: unknown): value is SidebarState {
  return value === 'expanded' || value === 'collapsed';
}

@Injectable()
export class SidebarService {
  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private readonly _setting: SignalSetting<SidebarState> = createSignalSetting<SidebarState>({
    key: SIDEBAR_STORAGE_KEY,
    defaultValue: 'expanded',
    validate: isSidebarState,
  });

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly state: Signal<SidebarState> = this._setting.value;

  readonly isExpanded = computed<boolean>(() => this.state() === 'expanded');

  readonly isCollapsed = computed<boolean>(() => this.state() === 'collapsed');

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggles the sidebar between expanded and collapsed.
   *
   * @returns {void}
   */
  toggle(): void {
    this._setting.update((state) => (state === 'expanded' ? 'collapsed' : 'expanded'));
  }

  /**
   * Sets the sidebar state to the specified value.
   *
   * @param state - The new state of the sidebar, either 'expanded' or 'collapsed'.
   * @returns {void}
   */
  set(state: SidebarState): void {
    this._setting.set(state);
  }
}
